import { useLogout } from '@Hooks/useMsal';
import {
    ApiUserPartnershipInformation,
    EnumAccessState,
    EnumPartnershipConsent,
    getPartnershipTypeShortLabel,
} from '@Model';
import { Badge, Button, FlexColumn, FlexRow, useTranslate } from '@synqo/frontend-component-library';
import { useMemo } from 'react';

interface IClientSelectionProps {
    client: string;
    partnerships: ApiUserPartnershipInformation[];
    hasOnlyOneClient: boolean;
    backToClientSelection: () => void;
    selectPartnership: (partnership: ApiUserPartnershipInformation) => void;
}

function SelectionFooter({
    hasOnlyOneClient,
    backToClientSelection,
    variant,
}: {
    hasOnlyOneClient: boolean;
    backToClientSelection: () => void;
    variant?: 'secondary';
}) {
    const logout = useLogout();

    return hasOnlyOneClient ? (
        <Button appearance="outlined" variant={variant} onClick={logout}>
            Logout
        </Button>
    ) : (
        <Button appearance="outlined" variant={variant} onClick={backToClientSelection}>
            Zurück
        </Button>
    );
}

function partnershipTooltip(partnership: ApiUserPartnershipInformation): string | null {
    if (partnership.access_state === EnumAccessState.Locked) {
        return 'Dieser Mandant ist aktuell nicht verfügbar.';
    }
    if (partnership.partnership_consent === EnumPartnershipConsent.Undecided) {
        return 'Sie müssen diesen Zugang noch bestätigen.';
    }
    return null;
}

export function MandantSelection({
    client,
    partnerships,
    hasOnlyOneClient,
    backToClientSelection,
    selectPartnership,
}: IClientSelectionProps) {
    const i18n = useTranslate('');

    // A mandant can expose more than one partnership (e.g. a portal mandant has both an internal
    // admin partnership and the portal partnership). They share the same mandant name, so append
    // the partnership type to keep the buttons distinguishable.
    const partnershipsPerMandant = useMemo(() => {
        const counts = new Map<string, number>();
        for (const p of partnerships) {
            counts.set(p.mandant_id, (counts.get(p.mandant_id) ?? 0) + 1);
        }
        return counts;
    }, [partnerships]);

    const partnershipLabel = (partnership: ApiUserPartnershipInformation) => {
        if ((partnershipsPerMandant.get(partnership.mandant_id) ?? 0) <= 1) {
            return partnership.mandant_display;
        }
        const typeLabel = i18n(getPartnershipTypeShortLabel(partnership.type));
        return `${partnership.mandant_display} (${typeLabel})`;
    };

    if (partnerships.length === 0) {
        return (
            <FlexColumn>
                <h2>{client}</h2>
                <p>{i18n('Sie haben auf diesen Kunden keinen Zugriff!')}</p>
                <SelectionFooter hasOnlyOneClient={hasOnlyOneClient} backToClientSelection={backToClientSelection} />
            </FlexColumn>
        );
    }

    return (
        <FlexColumn>
            <b>{client}</b>
            <small>{i18n('Bitte wählen Sie einen Mandanten')}</small>
            {partnerships.map((partnership) => (
                <Button
                    key={partnership.partnership_id}
                    appearance="outlined"
                    onClick={() => selectPartnership(partnership)}
                    locked={partnership.access_state == EnumAccessState.Locked}
                    tooltip={partnershipTooltip(partnership)}
                >
                    <FlexRow align="center" gap="xs">
                        {partnershipLabel(partnership)}
                        {partnership.partnership_consent === EnumPartnershipConsent.Undecided && (
                            <Badge variant="accent" withDot>
                                {i18n('Neu')}
                            </Badge>
                        )}
                    </FlexRow>
                </Button>
            ))}
            <SelectionFooter
                hasOnlyOneClient={hasOnlyOneClient}
                backToClientSelection={backToClientSelection}
                variant="secondary"
            />
        </FlexColumn>
    );
}
